import React, {useEffect, useRef, useState} from "react";
import {ArrowLeft, ChevronUp, Clock, Receipt, User} from "lucide-react";
import {formatMoney} from "./PosModals";

// Caisse web sur téléphone (< 768 px) — même découpage que la caisse mobile
// (pos_clone DashboardScreen, isPhone) : les produits occupent l'écran, un
// pied fixe porte les raccourcis (client, tickets en attente), la ligne
// « Ticket courant » qui ouvre le panier plein écran, et le gros bouton Payer.

export const PHONE_QUERY = "(max-width: 767px)";

// true sous 768 px. Sans matchMedia (tests, vieux navigateurs) : disposition
// tablette / ordinateur.
export const usePhoneLayout = () => {
    const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
        && window.matchMedia(PHONE_QUERY).matches;
    const [isPhone, setIsPhone] = useState(get);
    useEffect(() => {
        if (typeof window.matchMedia !== "function") return undefined;
        const mq = window.matchMedia(PHONE_QUERY);
        const onChange = () => setIsPhone(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener("change", onChange);
        else mq.addListener?.(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", onChange);
            else mq.removeListener?.(onChange);
        };
    }, []);
    return isPhone;
};

const Chip = ({active, icon: Icon, label, badge, onClick, ariaLabel}) => (
    <button type="button" onClick={onClick} aria-label={ariaLabel || label}
            className={`relative min-w-0 flex items-center justify-center gap-1.5 min-h-[44px] px-3.5 rounded-full border-[1.5px] border-primary text-sm font-semibold transition active:scale-[0.97] ${
                active ? "bg-primary text-white" : "bg-transparent text-primary"}`}>
        <Icon className="w-4 h-4 flex-shrink-0"/>
        <span className="truncate">{label}</span>
        {badge > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center tabular-nums">
                {badge}
            </span>
        )}
    </button>
);

export const PhoneCartBar = ({sale, totals, heldCount, canPay, onClient, onHeld, onOpenBasket, onPay}) => {
    const count = totals.count;
    // Petit rebond du compteur à chaque article ajouté : le panier n'est pas
    // visible, c'est le seul retour quand on touche un produit.
    const [bump, setBump] = useState(false);
    const prev = useRef(count);
    useEffect(() => {
        if (count > prev.current) {
            setBump(true);
            const t = setTimeout(() => setBump(false), 220);
            prev.current = count;
            return () => clearTimeout(t);
        }
        prev.current = count;
        return undefined;
    }, [count]);

    return (
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 pb-[env(safe-area-inset-bottom)]">
            <div className="flex gap-2 px-4 pt-3 pb-2 border-b border-gray-200/60 dark:border-gray-700/60">
                <Chip icon={User} active={!!sale.client} onClick={onClient}
                      label={sale.client ? sale.client.name : "Client"}
                      ariaLabel={sale.client ? `Client : ${sale.client.name}` : "Choisir un client"}/>
                <Chip icon={Clock} label="En attente" badge={heldCount} onClick={onHeld}
                      ariaLabel={`Tickets en attente (${heldCount})`}/>
            </div>

            <button type="button" onClick={onOpenBasket} aria-label={`Ouvrir le ticket courant (${count} articles)`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-100 dark:active:bg-gray-700">
                <Receipt className="w-[22px] h-[22px] text-gray-800 dark:text-gray-100 flex-shrink-0"/>
                <span className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-gray-900 dark:text-white">Ticket courant</span>
                    <span className="block text-[13px] text-gray-500 dark:text-gray-400">
                        <span className={`inline-block tabular-nums transition-transform duration-200 ${bump ? "scale-125 text-primary font-bold" : ""}`}>
                            {count}
                        </span>
                        {" "}article{count !== 1 ? "s" : ""}{sale.lines.length ? ` · ${formatMoney(totals.total)}` : ""}
                    </span>
                </span>
                <ChevronUp className="w-[22px] h-[22px] text-gray-400"/>
            </button>

            <button type="button" onClick={onPay} disabled={!canPay}
                    className="w-full py-5 bg-primary text-white text-xl font-bold tracking-[0.5px] tabular-nums active:bg-primary-dark disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed">
                {sale.lines.length ? `Payer ${formatMoney(totals.total)}` : "Payer"}
            </button>
        </div>
    );
};

// Panier plein écran : en-tête « ← Ticket (n) », puis le panier complet
// (lignes, totaux, bouton Payer). Les fenêtres (client, remise, paiement…)
// s'ouvrent par-dessus (z-50).
export const PhoneBasket = ({count, onClose, children}) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setMounted(true));
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            cancelAnimationFrame(frame);
            document.body.style.overflow = previous;
        };
    }, []);

    return (
        <div role="dialog" aria-modal="true" aria-label="Ticket courant"
             className={`fixed inset-0 z-40 flex flex-col bg-white dark:bg-gray-800 pt-[env(safe-area-inset-top)] transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                 mounted ? "translate-y-0" : "translate-y-full"}`}>
            <div className="flex items-center justify-between px-3 py-3.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <button type="button" onClick={onClose} aria-label="Retour aux produits"
                        className="w-10 h-10 rounded-full flex items-center justify-center text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <ArrowLeft className="w-6 h-6"/>
                </button>
                <h2 className="text-[17px] font-bold text-gray-900 dark:text-white">Ticket ({count})</h2>
                <span className="w-10"/>
            </div>
            <div className="flex-1 min-h-0 flex flex-col pb-[env(safe-area-inset-bottom)]">{children}</div>
        </div>
    );
};
